import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowRight, Check, Dumbbell, ShieldCheck, Sparkles } from 'lucide-react';

import { Button, Field, Input, Select, Stack } from '../components/ui-kit';
import { BrandLogoMark, BrandLogoWithWordmark } from '../components/BrandLogo';

const AUTH_ERROR_CODES = Object.freeze({
  TWO_FACTOR_REQUIRED: 'two_factor_required',
  INVALID_TWO_FACTOR_CODE: 'invalid_two_factor_code',
});

const PENDING_EMAIL_STORAGE_KEY = 'td:pending-email-verification';

const savePendingVerificationEmail = (email) => {
  if (typeof window === 'undefined') return;
  const normalizedEmail = (email || '').trim();
  if (!normalizedEmail) return;
  try {
    window.sessionStorage.setItem(PENDING_EMAIL_STORAGE_KEY, normalizedEmail);
  } catch (error) {
    // Storage can be unavailable in private or embedded contexts.
  }
};

const extractErrorDetail = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  const message = error?.response?.data?.message;
  return typeof message === 'string' ? message : '';
};

const extractErrorCode = (error) => {
  const data = error?.response?.data;
  if (typeof data?.error_code === 'string') return data.error_code;
  if (typeof data?.detail?.error_code === 'string') return data.detail.error_code;
  return '';
};

export const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: '',
    twoFactorCode: '',
  });
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRateLimitError = (error) => {
    const status = error?.response?.status;
    if (status !== 429) return false;
    const rawRetryAfter =
      error?.response?.data?.retry_after ?? error?.response?.data?.detail?.retry_after;
    const retryAfter = Number.parseInt(rawRetryAfter, 10);
    const safeRetryAfter =
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60;
    setRetryAfterSeconds(safeRetryAfter);
    toast.error(
      `${extractErrorDetail(error) || t('auth.authError')} (${safeRetryAfter}s)`
    );
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (retryAfterSeconds > 0 || submitting) return;
    setSubmitting(true);
    try {
      let role = 'athlete';

      if (isLogin) {
        const payload = { email: formData.email, password: formData.password };
        if (needsTwoFactor) {
          payload.two_factor_code = formData.twoFactorCode.trim();
        }
        const data = await login(payload);
        role = data?.user?.role || role;
        setNeedsTwoFactor(false);
        setFormData((prev) => ({ ...prev, twoFactorCode: '' }));
        toast.success(t('auth.loginSuccess'));
      } else {
        if (!formData.role) {
          toast.error(t('auth.selectRole'));
          return;
        }
        if (!['athlete', 'coach', 'club'].includes(formData.role)) {
          toast.error(t('auth.selectRole'));
          return;
        }

        const registeredEmail = formData.email.trim();
        const data = await register({
          email: registeredEmail,
          password: formData.password,
          name: formData.name,
          role: formData.role,
        });
        savePendingVerificationEmail(registeredEmail);
        toast.success(t('auth.registerSuccess'));
        navigate('/check-email', {
          replace: true,
          state: {
            email: registeredEmail,
            verificationEmailSent: data?.verification_email_sent === true,
          },
        });
        return;
      }

      if (role === 'coach') {
        navigate('/coach/dashboard');
      } else if (role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      if (handleRateLimitError(error)) return;
      const status = error?.response?.status;
      const errorCode = extractErrorCode(error);
      const errorDetail = extractErrorDetail(error);
      if (
        isLogin &&
        status === 401 &&
        errorCode === AUTH_ERROR_CODES.TWO_FACTOR_REQUIRED
      ) {
        setNeedsTwoFactor(true);
        toast.error(t('auth.twoFactorRequired'));
        return;
      }
      if (
        isLogin &&
        needsTwoFactor &&
        status === 401 &&
        errorCode === AUTH_ERROR_CODES.INVALID_TWO_FACTOR_CODE
      ) {
        toast.error(t('auth.authError'));
        return;
      }
      toast.error(errorDetail || t('auth.authError'));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (retryAfterSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setRetryAfterSeconds((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [retryAfterSeconds]);

  const isLocked = retryAfterSeconds > 0;
  const submitLabel = isLocked
    ? t('auth.retryAfterSeconds', { seconds: retryAfterSeconds })
    : isLogin
    ? t('auth.login')
    : t('auth.register');
  const roleOptions = [
    {
      value: 'athlete',
      label: t('auth.roleAthlete'),
      description: t('auth.roleAthleteHint', 'Track training, matches, and progress.'),
    },
    {
      value: 'coach',
      label: t('auth.roleCoach'),
      description: t('auth.roleCoachHint', 'Guide athletes and manage sessions.'),
    },
    {
      value: 'club',
      label: t('auth.roleClub'),
      description: t('auth.roleClubHint', 'Coordinate club programs and teams.'),
    },
  ];

  return (
    <div className="td-auth-redesign">
      <section className="td-auth-redesign__brand" aria-label="Tennis Diary">
        <div className="td-auth-redesign__court" aria-hidden="true" />
        <div className="td-auth-redesign__brand-inner">
          <BrandLogoWithWordmark size={52} className="td-auth-redesign__logo" />
          <div className="td-auth-redesign__eyebrow">
            <Sparkles size={16} strokeWidth={1.7} />
            <span>{t('auth.brandKicker', 'Athlete diary')}</span>
          </div>
          <h1 data-testid="auth-title">Tennis Diary</h1>
          <p>{t('auth.heroTitle', 'Train with momentum. Track every session.')}</p>
          <div className="td-auth-redesign__metrics" aria-hidden="true">
            <span>01 {t('auth.proofPlan', 'Plan')}</span>
            <span>02 {t('auth.proofTrack', 'Track')}</span>
            <span>03 {t('auth.proofImprove', 'Improve')}</span>
          </div>
        </div>
      </section>

      <main className="td-auth-redesign__main">
        <div className="td-auth-redesign__card">
          <div className="td-auth-redesign__mobile-logo">
            <BrandLogoMark size={48} />
          </div>

          <div className="td-auth-redesign__tabs" role="tablist" aria-label={t('auth.authMode', 'Authentication mode')}>
            <button
              type="button"
              role="tab"
              aria-selected={isLogin}
              className={isLogin ? 'is-active' : ''}
              onClick={() => setIsLogin(true)}
              data-testid="auth-mode-login"
            >
              {t('auth.login')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isLogin}
              className={!isLogin ? 'is-active' : ''}
              onClick={() => setIsLogin(false)}
              data-testid="auth-mode-register"
            >
              {t('auth.register')}
            </button>
          </div>

          <div className="td-auth-redesign__heading">
            <span>
              <Dumbbell size={16} strokeWidth={1.7} />
              {isLogin
                ? t('auth.loginKicker', 'Athlete access')
                : t('auth.registerKicker', 'Create account')}
            </span>
            <h2>
              {isLogin
                ? t('auth.loginTitle', 'Welcome back')
                : t('auth.registerTitle', 'Choose your lane')}
            </h2>
            <p>
              {isLogin
                ? t('auth.loginSubtitle', 'Continue tracking your tennis progress and training rhythm.')
                : t('auth.registerSubtitle', 'Start with the role that matches how you use Tennis Diary.')}
            </p>
          </div>

          <form className="td-auth-redesign__form" onSubmit={handleSubmit} data-testid="auth-form" noValidate>
            <Stack gap={4}>
              {!isLogin && (
                <Field label={t('auth.name')} required>
                  <Input
                    type="text"
                    data-testid="input-name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </Field>
              )}

              <Field label={t('auth.email')} required>
                <Input
                  type="email"
                  data-testid="input-email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </Field>

              <Field label={t('auth.password')} required>
                <Input
                  type="password"
                  data-testid="input-password"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
              </Field>

              {isLogin && (
                <div className="flex justify-end">
                  <Button as={Link} to="/forgot-password" variant="link" size="sm">
                    {t('auth.forgotPassword', 'Forgot password?')}
                  </Button>
                </div>
              )}

              {!isLogin && (
                <div className="td-auth-redesign__roles">
                  <Field label={t('auth.role')} required>
                    <Select
                      className="td-auth-redesign__role-native"
                      data-testid="input-role"
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({ ...formData, role: e.target.value })
                      }
                      required
                    >
                      <option value="">{t('auth.selectRole')}</option>
                      {roleOptions.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <div className="td-auth-redesign__role-grid" aria-label={t('auth.role')}>
                    {roleOptions.map((role) => {
                      const selected = formData.role === role.value;
                      return (
                        <button
                          type="button"
                          key={role.value}
                          className={selected ? 'is-selected' : ''}
                          onClick={() => setFormData({ ...formData, role: role.value })}
                          aria-pressed={selected}
                          data-testid={`role-card-${role.value}`}
                        >
                          <span>{selected ? <Check size={14} strokeWidth={2} /> : null}</span>
                          <strong>{role.label}</strong>
                          <small>{role.description}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isLogin && needsTwoFactor && (
                <Field label={t('auth.twoFactorCode')} required>
                  <Input
                    type="text"
                    data-testid="input-two-factor-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={formData.twoFactorCode}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        twoFactorCode: e.target.value,
                      })
                    }
                    required
                  />
                </Field>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                loading={submitting}
                disabled={isLocked}
                data-testid="auth-submit-button"
                rightIcon={!submitting ? <ArrowRight size={18} strokeWidth={1.7} /> : null}
                className="td-auth-redesign__submit"
              >
                {submitLabel}
              </Button>
            </Stack>
          </form>

          <div className="td-auth-redesign__footer">
            <span>
              <ShieldCheck size={16} strokeWidth={1.7} />
              {t('auth.securityNote', 'Secure web and mobile session')}
            </span>
            <p>
              {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
              <Button
                as="button"
                variant="link"
                size="sm"
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? t('auth.register') : t('auth.login')}
              </Button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
